import ko from "knockout";
import $ from "jquery";
import I18n from "i18n-js";
import Popover from "bootstrap/js/dist/popover";
class SettingsEditor {
    constructor(view_name) {
        this.view_name = view_name;
        this.properties = ko.observableArray([]);
        this.changed = ko.computed(() => {
            return this.properties().length > 0;
        });
    }
    setKey(key, val) {
        this[key] = val;
        this.properties.push(key);
    }
    reset() {
        this.properties([]);
    }
    toJS() {
        const data = {};
        for (let p of this.properties()) {
            let key;
            if (p.indexOf(".") === 0) {
                key = `${this.view_name}${p}`;
            }
            else {
                key = p;
            }
            data[key] = this[p];
        }
        return data;
    }
}
class TranslationEditor extends SettingsEditor {
    edit(input) {
        const self = this;
        const modalToggleButton = input.parent('button[data-toggle="modal"]');
        self.preventModalTrigger("on", input, modalToggleButton);
        if (!input.data("is_bound")) {
            const val = input.data("editable-text");
            self[val] = input.html();
            input.on("blur", function () {
                const v = self.stripTags(input.html());
                input.html(v);
                if (self[val] !== v) {
                    self.setKey(val, v);
                }
                self.preventModalTrigger("off", input, modalToggleButton);
            });
            input.data("is_bound", true);
        }
    }
    stripTags(html) {
        return html.replace(/<(?!br\s*\/?)[^>]+>/g, "").replace("%<br", "% <br");
    }
    // Prevent parent button from triggering the modal with spacebar key while in text editing mode.
    // This fix works with modals that are initialized by buttons with `data-toggle` and `data-target` attributes.
    preventModalTrigger(mode, _input, modalToggleButton) {
        if (mode !== "on" && mode !== "off") {
            throw new Error(mode +
                " is not a correct mode for preventModalTrigger (only on or off are allowed).");
        }
        // If attribute with an actual value is empty, then it means replacement was already done or is not needed.
        // This check is required, because some events that trigger preventModalTrigger function may occur more than once, e.g. double-clicking.
        if (!modalToggleButton.length ||
            typeof modalToggleButton.attr("data-target") !== "string" ||
            modalToggleButton.attr(mode === "on" ? "data-target" : "data-target-original") === "") {
            return;
        }
        const dataTargetVal = modalToggleButton.attr("data-target");
        const dataTargetOriginalVal = modalToggleButton.attr("data-target-original");
        modalToggleButton.attr({
            "data-target": mode === "on" ? "" : dataTargetOriginalVal,
            "data-target-original": mode === "on" ? dataTargetVal : "",
        });
    }
}
class LinkEditor extends SettingsEditor {
    edit(link) {
        if (!link.data("is_bound")) {
            const options = {
                html: true,
                sanitize: false,
                title: `${I18n.t("editor.link_editor.title")} <button type='button' class='close' aria-label='${I18n.t("editor.cancel")}'><span aria-hidden='true'>&times;</span></button>`,
                trigger: "manual",
                content: $("#link_editor").html(),
                placement: "auto",
                container: link.attr("data-container") || link.data("container") || "body",
            };
            new Popover(link[0], options);
            this.handlePopover(link);
            link.data("is_bound", true);
        }
        const linkPopover = Popover.getOrCreateInstance(link[0]);
        linkPopover.toggle();
    }
    // Set new link value.
    setNewLink(link, value) {
        link.attr("href", value);
        this.setKey(link.data("editable-link"), value);
    }
    // Perform URL validation using native HTML5 validation, treat javascript protocol as invalid.
    isValidUrl(input, value) {
        return !!(typeof value === "string" &&
            input[0].checkValidity() &&
            value.toLowerCase().indexOf("javascript:") === -1);
    }
    // Add proper validation state to the markup.
    setValidationFeedback(feedbackGroup, validationState) {
        const formControlFeedback = feedbackGroup.find(".form-control-feedback");
        if (validationState === "initial") {
            feedbackGroup
                .addClass("hidden")
                .removeClass("has-success")
                .removeClass("has-error");
            formControlFeedback.removeClass("icon-ok").removeClass("icon-remove");
        }
        else if (validationState === "valid") {
            feedbackGroup.removeClass("has-error").addClass("has-success");
            formControlFeedback.removeClass("icon-remove").addClass("icon-ok");
        }
        else if (validationState === "invalid") {
            feedbackGroup.removeClass("has-success").addClass("has-error");
            formControlFeedback.removeClass("icon-ok").addClass("icon-remove");
        }
    }
    // Set initial state of the link editor.
    initEditorState(linkEditor, link) {
        const isLinkOnSelectList = (val, select) => Array.prototype.find.call(select[0].options, (option) => option.value === val);
        const select = linkEditor.find("select");
        const customUrlInput = linkEditor.find("input");
        const placeholderOption = select.find('option[value=""]');
        const linkValue = decodeURI(link.attr("href").replace(/&amp;/g, "&").replace(location.search, ""));
        const specialDefaultLinkObj = typeof link.data("default-link") === "object"
            ? link.data("default-link")
            : {};
        const specialDefaultLink = typeof specialDefaultLinkObj.url === "string" &&
            (!isLinkOnSelectList(specialDefaultLinkObj.url, select) ||
                specialDefaultLinkObj.url === "")
            ? specialDefaultLinkObj.url
            : false;
        const specialDefaultLinkLabel = typeof specialDefaultLinkObj.label === "string"
            ? specialDefaultLinkObj.label
            : "";
        const isCustomUrl = linkValue !== specialDefaultLink &&
            !isLinkOnSelectList(linkValue, select);
        if (typeof specialDefaultLink === "string") {
            placeholderOption.attr({
                value: specialDefaultLink,
                hidden: false,
                disabled: false,
            });
            if (specialDefaultLinkLabel) {
                placeholderOption.text(specialDefaultLinkLabel);
            }
        }
        else {
            placeholderOption.remove();
        }
        if (isCustomUrl) {
            select.val("custom-url");
            customUrlInput.val(linkValue);
        }
        else {
            select.val(linkValue);
        }
        this.toggleCustomUrlEditor(isCustomUrl, linkEditor);
    }
    // Enable or disable custom URL editor.
    toggleCustomUrlEditor(isCustomUrl, linkEditor) {
        const customUrlEditor = linkEditor.find(".custom-url-editor");
        const customUrlInput = linkEditor.find("input");
        if (isCustomUrl) {
            customUrlInput.prop("required", true);
            customUrlEditor.removeClass("hidden");
        }
        else {
            customUrlInput.val("").prop("required", false);
            customUrlEditor.addClass("hidden");
            this.setValidationFeedback(customUrlEditor, "initial");
        }
    }
    // Handle popover with link editor.
    handlePopover(link) {
        const self = this;
        link[0].addEventListener("inserted.bs.popover", function () {
            const popover = $("#" + link.attr("aria-describedby"));
            const popoverClose = popover.find(".close");
            const select = popover.find("select");
            const linkEditor = popover.find(".link-editor");
            const customUrlEditor = linkEditor.find(".custom-url-editor");
            const customUrlInput = linkEditor.find("input");
            self.initEditorState(linkEditor, link);
            popoverClose.on("click", () => {
                var _a;
                (_a = Popover.getOrCreateInstance(link[0])) === null || _a === void 0 ? void 0 : _a.hide();
            });
            select.on("change", function () {
                const val = select.val();
                const isCustomUrl = val === "custom-url";
                self.toggleCustomUrlEditor(isCustomUrl, linkEditor);
                if (!isCustomUrl) {
                    self.setNewLink(link, val);
                }
            });
            customUrlInput.on("keyup", function (e) {
                const customUrlValue = encodeURI(e.target.value);
                const isValid = self.isValidUrl(customUrlInput, customUrlValue);
                if (isValid) {
                    self.setNewLink(link, customUrlValue);
                }
                self.setValidationFeedback(customUrlEditor, isValid ? "valid" : "invalid");
            });
        });
    }
}
class TagEditor extends SettingsEditor {
    edit(tag) {
        const popoverTarget = tag.find(".editable-tag-area");
        if (!tag.data("is_bound")) {
            const options = {
                html: true,
                sanitize: false,
                title: `${I18n.t("editor.tag_editor.title")} <button type='button' class='close js-cancel' title='${I18n.t("editor.cancel")}'><span aria-hidden='true'>&times;</span></button>`,
                trigger: "manual",
                content: $("#tag_editor").html(),
                placement: "auto",
                container: "body",
            };
            new Popover(popoverTarget[0], options);
            this.handlePopover(tag, popoverTarget);
            tag.data("is_bound", true);
        }
        const popoverInstance = Popover.getOrCreateInstance(popoverTarget[0]);
        popoverInstance.toggle();
    }
    handlePopover(tag, popoverTarget) {
        if (popoverTarget == null) {
            popoverTarget = tag;
        }
        const self = this;
        popoverTarget[0].addEventListener("inserted.bs.popover", function () {
            const popover = $("#" + popoverTarget.attr("aria-describedby"));
            const saveBtn = popover.find(".js-save");
            const cancelBtn = popover.find(".js-cancel");
            const select = popover.find("select");
            const editableTag = tag.data("editable-tag");
            const currentTag = tag[0].tagName.toLowerCase();
            select.attr("title", editableTag);
            select.val(currentTag);
            saveBtn.on("click", function () {
                var _a;
                const newTagValue = select.val();
                const newTagForUser = newTagValue
                    ? newTagValue
                    : I18n.t("editor.tag_editor.default");
                if (confirm(currentTag +
                    " \u2192 " +
                    newTagForUser +
                    "\n" +
                    I18n.t("editor.tag_editor.confirm"))) {
                    (_a = Popover.getOrCreateInstance(popoverTarget[0])) === null || _a === void 0 ? void 0 : _a.hide();
                    self.setKey(editableTag, newTagValue);
                }
                else {
                    select.val(currentTag);
                }
            });
            cancelBtn.click(function () {
                var _a;
                select.val(currentTag);
                (_a = Popover.getOrCreateInstance(popoverTarget[0])) === null || _a === void 0 ? void 0 : _a.hide();
            });
        });
    }
}
export class ContentEditor {
    constructor() {
        this.translations = new TranslationEditor(Skubacz.configuration.view_name);
        this.links = new LinkEditor(Skubacz.configuration.view_name);
        this.tags = new TagEditor(Skubacz.configuration.view_name);
        this.changed = ko.computed(() => this.translations.changed() || this.links.changed() || this.tags.changed());
        this.save_in_progress = ko.observable(false);
        const { token } = this.params();
        if (token) {
            $.ajaxSetup({
                headers: { Authorization: `Token token=\"${token}\"` },
            });
        }
        this.tags.changed.subscribe(() => {
            const result = this.save();
            if (result) {
                result.done(() => {
                    window.location.reload();
                });
            }
        });
    }
    params() {
        const pairs = window.location.search.substring(1).split("&");
        const obj = {};
        for (let p of pairs) {
            if (!p) {
                continue;
            }
            const pair = p.split("=");
            obj[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
        }
        return obj;
    }
    url() {
        return Skubacz.configuration.inline_edit_url;
    }
    supports(data) {
        return data.action === "saveText";
    }
    handle(_data) {
        return this.save();
    }
    save() {
        if (this.save_in_progress()) {
            return false;
        }
        if (!this.changed()) {
            return false;
        }
        const data = {
            authentication_token: Skubacz.configuration.authenticity_token,
            locale: I18n.locale,
            translations: this.translations.toJS(),
            links: Object.assign({}, this.links.toJS(), this.tags.toJS()),
        };
        this.save_in_progress(true);
        return $.ajax({
            type: "PUT",
            url: this.url(),
            contentType: "application/json",
            dataType: "json",
            data: JSON.stringify(data),
            success: (r) => {
                if (r.success) {
                    this.translations.reset();
                    this.links.reset();
                    this.tags.reset();
                }
                else {
                    alert(`Błąd zapisu! ${JSON.stringify(r.errors)}`);
                }
                this.save_in_progress(false);
            },
            error: () => {
                this.save_in_progress(false);
            },
        });
    }
}
export class MessageListener {
    constructor(editors) {
        this.editors = editors;
        window.onmessage = (event) => this.onMessage(event);
        $(document).scrollTop(sessionStorage["editorScrollTop"] || 0);
    }
    onMessage(event) {
        if (!event.data.action) {
            return;
        }
        const editors = this.editors.filter((e) => e.supports(event.data));
        if (editors.length > 0) {
            const result = [];
            for (let e of editors) {
                result.push(e.handle(event.data));
            }
            return result;
        }
        else {
            sessionStorage["editorScrollTop"] = $(document).scrollTop();
            window.location.reload();
            return;
        }
    }
}
class FieldEditor {
    constructor(type) {
        this.type = type;
        this.ready();
    }
    supports(data) {
        return (data.action === "fieldChanged" &&
            $(`[data-editor-field=${data.field}]`).data("editor-type") === this.type);
    }
    elements() {
        return $(`[data-editor-type='${this.type}']`);
    }
    ready() { }
}
class ImageEditor extends FieldEditor {
    constructor() {
        super("image");
    }
    handle(data) {
        const { field } = data;
        const $img = $(`img[data-editor-field=${field}]`);
        const imgSrcData = getImgData($img.attr("src"), data.value);
        if (imgSrcData.fileUrl.match(/svg|pdf/)) {
            $img.attr("src", imgSrcData.fileUrl);
        }
        else {
            $img.attr("src", imgSrcData.fileUrl + imgSrcData.params);
            updateSrcset($img, data.value);
        }
    }
    setPosition(image, editor) {
        editor.width(image.width());
        //    editor.height(image.height())
        const io = image.offset();
        editor.offset(io);
    }
    ready() {
        const self = this;
        this.elements().each(function () {
            let image = $(this);
            const parent = image.parent();
            if (!parent.hasClass("data-editor-image")) {
                const imageEditor = $($("#image_editor_overlay").html());
                imageEditor.find("a").on("click", function (e) {
                    image = $(imageEditor).siblings("img[data-editor-type=image]:visible");
                    self.onClick(e, image);
                });
                if (parent.css("position") === "static") {
                    parent.css("position", "relative");
                }
                parent.addClass("data-editor-image").prepend(imageEditor);
                self.setPosition(image, imageEditor);
                image.one("mouseover", () => self.setPosition(image, imageEditor));
            }
        });
    }
    onClick(e, image) {
        const field = image.data("editor-field");
        console.log(`Image editor click! ${field}`);
        window.top.postMessage({ action: "editClick", field }, "*");
        e.preventDefault();
        e.stopPropagation();
    }
}
function getImgData(url, value) {
    const currentSplited = splitUrl(url);
    const currentQuery = currentSplited.queryString;
    const currentParams = getQueryParams(currentQuery);
    const valueSplited = splitUrl(value);
    const valueQuery = valueSplited.queryString;
    const valueParams = getQueryParams(valueQuery, { fit: currentParams.fit });
    return {
        fileUrl: valueSplited.path,
        params: $.param(Object.assign({}, currentParams, valueParams)),
    };
}
var updateSrcset = function ($img, value) {
    const $pictureEl = $img.parent("picture");
    const $elements = $pictureEl.length
        ? $img.add($pictureEl.children("source"))
        : $img;
    const getNewSrcset = function (srcset) {
        const newSrcsetArr = [];
        srcset.split(",").forEach(function (el) {
            const img = el.trim().split(" ");
            const imgUrl = img[0];
            const imgDescriptor = img[1];
            const imgData = getImgData(imgUrl, value);
            newSrcsetArr.push(imgData.fileUrl +
                imgData.params +
                (imgDescriptor ? " " + imgDescriptor : ""));
        });
        return newSrcsetArr.join(", ");
    };
    $elements.each(function (_i, el) {
        if (el.srcset) {
            el.srcset = getNewSrcset(el.srcset);
        }
    });
};
var splitUrl = function (url) {
    const pathLength = url.indexOf("?") === -1 ? url.length : url.indexOf("?") + 1;
    const path = url.slice(0, pathLength);
    const queryString = url.slice(pathLength);
    return {
        path: decodeURIComponent(path),
        queryString: decodeURIComponent(queryString),
    };
};
var getQueryParams = function (query, params = {}) {
    if (query.length === 0) {
        return {};
    }
    else {
        return query.split("&").reduce(function (map, obj) {
            const pair = obj.split("=");
            map[pair[0]] = params[pair[0]] || pair[1];
            return map;
        }, {});
    }
};
const swapBackgroundImage = (bi, value) => bi
    .split(/url\s?\(/)
    .map((x) => {
    return x
        .split(/\)/)
        .map((y) => {
        if (y.indexOf("assets") !== -1) {
            return value;
        }
        else if (y.indexOf("uploads") !== -1) {
            const current = y.replace(/"|'/g, "");
            const currentSplited = splitUrl(current);
            const currentQuery = currentSplited.queryString;
            const currentParams = getQueryParams(currentQuery);
            const valueSplited = splitUrl(value);
            const valueQuery = valueSplited.queryString;
            const valueParams = getQueryParams(valueQuery, {
                fit: currentParams.fit,
            });
            const fileUrl = valueSplited.path;
            const params = $.param(Object.assign({}, currentParams, valueParams));
            if (fileUrl.match(/svg|pdf/)) {
                return fileUrl;
            }
            else {
                return fileUrl + params;
            }
        }
        else {
            return y;
        }
    })
        .join(")");
})
    .join("url(");
class BackgroundImageEditor extends FieldEditor {
    constructor() {
        super("background-image");
    }
    ready() {
        const self = this;
        this.elements().each(function () {
            const image = $(this);
            const overlay = $($("#image_editor_overlay").html());
            overlay.css("right", 0);
            overlay.find("a").click((e) => self.onClick(e, image));
            if (image.css("position") === "static") {
                image.css("position", "relative");
            }
            image.addClass("data-editor-image").prepend(overlay);
        });
    }
    onClick(e, image) {
        const field = image.data("editor-field");
        window.top.postMessage({ action: "editClick", field }, "*");
        e.preventDefault();
        e.stopPropagation();
    }
    handle(data) {
        const { field } = data;
        const { value } = data;
        $(`[data-editor-field='${field}']`).each(function () {
            const element = this;
            const style = window.getComputedStyle(element);
            const bi = style.getPropertyValue("background-image");
            element.style.setProperty("background-image", swapBackgroundImage(bi, value), "important");
        });
    }
}
const inIframe = function () {
    try {
        return window.self !== window.top;
    }
    catch (e) {
        return true;
    }
};
$(document).ready(function () {
    const editor = new ContentEditor();
    Skubacz.editor = editor;
    if (inIframe()) {
        editor.changed.subscribe((nevValue) => {
            window.top.postMessage({ action: "textChanged", value: nevValue }, "*");
        });
        const editors = [new BackgroundImageEditor(), new ImageEditor(), editor];
        Skubacz.listener = new MessageListener(editors);
        $("body").css("overflow-x", "hidden");
    }
    else {
        $("body").css("padding-top", "40px");
        $('<div id="site_editor_container" data-bind="template: \'site_editor\'"></div>').prependTo($("body"));
        ko.applyBindings(editor, $("#site_editor_container")[0]);
    }
    $("a[href^='/']").each(function () {
        $(this).attr("href", $(this).attr("href") + location.search);
    });
    // theme specific initialization
    const carousel = $(".carousel");
    if (carousel.length > 0 && carousel.carousel) {
        carousel.carousel("pause").on("slid", () => carousel.carousel("pause"));
    }
    document.querySelectorAll("[data-editable-text]").forEach((el) => {
        el.addEventListener("click", function (e) {
            editor.translations.edit($(el));
            e.preventDefault();
            e.stopPropagation();
            $(el).focus();
        }, true);
    });
});
$(document).on("click.editor.data-api", "[data-editable-link]", function (e) {
    const $modal = $(e.currentTarget).closest(".modal");
    const { editor } = Skubacz;
    if ($modal.length) {
        // Custom URL editor doesn't work within modal if popover container is set to body (focus issue with form elements inside a popover inside a modal).
        $(e.currentTarget).data("container", $modal);
    }
    editor.links.edit($(this));
    e.preventDefault();
    e.stopPropagation();
    return false;
});
$(document).on("click.editor.data-api", "[data-editable-tag]", function (e) {
    const { editor } = Skubacz;
    editor.tags.edit($(this));
    e.preventDefault();
    e.stopPropagation();
    return false;
});
$(document).on("click.editor.data-api", ".js-pin-navbar", function (e) {
    const navbar = document.getElementById("site_editor_navbar");
    const toggleButtons = document.querySelectorAll(".js-pin-navbar");
    navbar.classList.toggle("is-fixed");
    for (let el of toggleButtons) {
        el.classList.toggle("active");
    }
    return e.preventDefault();
});
